import { useQuery } from "@tanstack/react-query";
import { Eye, Trash2 } from "lucide-react";

import { listStarredAssets, type AssetCard, type StarredAssetsResponse } from "@/api/client";
import { AUDIENCE_LABEL } from "@/lib/asset-labels";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string | undefined;
  onView: (asset: AssetCard) => void;
  onUnstar: (asset: AssetCard) => void;
}

/** Right slide-out of the caller's starred assets, grouped by audience (the
 * mockup's "Enablement" / "Client Facing" buckets). Reads its own query so
 * it stays in sync with the live ledger; star mutations invalidate it. */
export function StarredDrawer({ open, onOpenChange, token, onView, onUnstar }: Props) {
  const starred = useQuery<StarredAssetsResponse>({
    queryKey: ["assets", "starred", token],
    queryFn: () => listStarredAssets(token!),
    enabled: open && !!token,
  });

  const groups: { label: string; items: AssetCard[] }[] = [
    { label: AUDIENCE_LABEL.my_learning, items: starred.data?.my_learning ?? [] },
    { label: AUDIENCE_LABEL.for_clients, items: starred.data?.for_clients ?? [] },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[360px] overflow-y-auto sm:max-w-[360px]">
        <SheetHeader>
          <SheetTitle>Bookmarks</SheetTitle>
          <SheetDescription>Your bookmarked resources, ready for quick access.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {starred.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading…
            </p>
          ) : (starred.data?.total ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              You haven't bookmarked anything yet. Use the bookmark button on a card.
            </p>
          ) : (
            groups
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <div key={g.label} className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {g.label}
                  </p>
                  {g.items.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">{asset.title}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="View"
                        onClick={() => onView(asset)}
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove bookmark"
                        onClick={() => onUnstar(asset)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  ))}
                </div>
              ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
